import styled from "styled-components";

import { ColumnChartGraph } from "@similarweb/pro-logic-components";
import { colorsPalettes, fontVariants } from "@similarweb/styles";

import { abbrNumberFilter } from "app/filters/ngFilters";

import type { CompareMetricResponse } from "../search-trends-page/common/types";

import { SearchVolumeOverTimeChangeIndicator } from "./SearchVolumeOverTimeChangeIndicator";
import { SearchVolumeOverTimeLoaderSkeleton } from "./SearchVolumeOverTimeLoaderSkeleton";
import { SearchVolumeOverTimeTotalVolumeDescription } from "./SearchVolumeOverTimeTotalVolumeDescription";

const CHART_HEIGHT = 180;
const CHART_WIDTH = 160;

const volumeFormatter = (value: number) => {
    return abbrNumberFilter()(value);
};

const dataLabels = {
    enabled: true,
    crop: false,
    overflow: "none",
    formatter: function () {
        return volumeFormatter(this.y);
    },
    style: {
        textOutline: "none",
        color: colorsPalettes.carbon[300],
        fontSize: fontVariants.dataTable2.size,
        fontWeight: fontVariants.dataTable2.weight,
        fontFamily: fontVariants.dataTable2.family,
    },
};

const TotalVolumeDescriptionWrapper = styled.div`
    margin-top: auto;
`;

type SearchVolumeOverTimeColumnChartProps = {
    isLoading: boolean;
    data: CompareMetricResponse;
};

export const SearchVolumeOverTimeColumnChart = ({ isLoading, data }: SearchVolumeOverTimeColumnChartProps) => {
    if (isLoading) {
        return <SearchVolumeOverTimeLoaderSkeleton />;
    }

    return (
        <>
            <SearchVolumeOverTimeChangeIndicator
                isLoading={isLoading}
                changeValue={data?.Volume?.Change}
                volumeGrowth={data?.TotalScore}
            />
            <ColumnChartGraph
                key={`${data?.Volume?.Values?.Interval}~${data?.Volume?.Values?.CompareInterval}`}
                data={[
                    {
                        name: "previous-period-column",
                        color: colorsPalettes.orange[400],
                        data: [[data?.Volume?.Values?.CompareInterval]],
                        visible: true,
                        // @ts-expect-error dataLabels is valid but not defined in the custom GenericGraphPropsSingleItem interface
                        dataLabels,
                    },
                    {
                        name: "current-period-column",
                        color: colorsPalettes.blue[400],
                        data: [[data?.Volume?.Values?.Interval]],
                        visible: true,
                        // @ts-expect-error dataLabels is valid but not defined in the custom GenericGraphPropsSingleItem interface
                        dataLabels,
                    },
                ]}
                shouldPresentLegend={false}
                graphConfig={{
                    chart: {
                        height: CHART_HEIGHT,
                        width: CHART_WIDTH,
                        marginTop: 0,
                        marginBottom: 0,
                        events: {
                            load: function () {
                                this.container.style.pointerEvents = "none";
                            },
                        },
                    },
                    plotOptions: {
                        column: {
                            pointWidth: 24,
                            stacking: null,
                        },
                    },
                    xAxis: {
                        labels: {
                            enabled: false,
                        },
                        lineWidth: 1,
                        lineColor: colorsPalettes.carbon[100],
                        tickLength: 0,
                        tickWidth: 0,
                        gridLineWidth: 0,
                    },
                    yAxis: {
                        visible: false,
                    },
                    tooltip: {
                        enabled: false,
                    },
                }}
            />
            <TotalVolumeDescriptionWrapper>
                <SearchVolumeOverTimeTotalVolumeDescription />
            </TotalVolumeDescriptionWrapper>
        </>
    );
};
